import React from "react";
import styled from "styled-components";
import { device, size } from "../../../constants";
import { useTheme } from "../../../contexts/ThemeContext";
import { socialNetworks } from "../../../data/contact-info";
import { InstagramLogo } from "../../assets/companies/instagram-logo";
import { LinkedinLogo } from "../../assets/companies/linkedin-logo";
import { WhatsappLogo } from "../../assets/companies/whatsapp-logo";
import { RelatedPosts } from "../RelatedPosts";
import BackToTopButton from "../buttons/backtop.button";
import { useWindowSize } from "../../../hooks/useWindowResize";
import { useTranslation } from "next-i18next";
import { Trans } from "react-i18next";

const ShareSection = styled.div`
  .container-blog {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  @media ${device.laptop} {
    .container-blog {
      align-items: flex-start;
    }
  }

  :is([data-theme="dark"] &) {
    background-color: ${({ theme }) => theme.colors["neutral-08"]};
  }
`;

const Title = styled.h2`
  font-size: 24px;
  line-height: 32px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors["neutral-05"]};
  margin: 16px 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 4px;
    background: ${({ theme }) => theme.colors["branding-04"]};
    border-radius: 100px;
  }

  @media ${device.laptop} {
    font-size: 28px;
    line-height: 48px;

    &::before {
      width: 48px;
      left: 0;
      transform: translate(0, -50%);
    }
  }

  :is([data-theme="dark"] &) {
    color: ${({ theme }) => theme.colors["neutral-01"]};

    &::before {
      background: ${({ theme }) => theme.colors["vivid-04"]};
    }
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  line-height: 32px;
  color: ${({ theme }) => theme.colors["neutral-05"]};
  font-weight: 400;
  text-align: center;

  @media ${device.laptop} {
    font-size: 22px;
    line-height: 40px;
  }

  :is([data-theme="dark"] &) {
    color: ${({ theme }) => theme.colors["neutral-01"]};
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 24px;
  margin-top: 20px;

  @media ${device.laptop} {
    gap: 40px;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
  text-decoration: none;

  :is([data-theme="dark"] &) {
    background-color: ${({ theme }) => theme.colors["neutral-08"]};
  }

  .circle {
    width: 56px;
    height: 56px;
    background: #F5F5F5;
    border-radius: 50%;
    padding: 12px;

    display: flex;
    align-items: center;
    justify-content: center;

    :is([data-theme="dark"] &) {
      background: ${({ theme }) => theme.colors["neutral-07"]};
    } 
  }

  span {
    color: ${({ theme }) => theme.colors["neutral-04"]};
    font-size: 14px;
    line-height: 100%;

    :is([data-theme="dark"] &) {
      color: ${({ theme }) => theme.colors["neutral-03"]};
    }
  }

  img {
    width: 100%;
  }

  @media ${device.laptop} {
    .circle {
      width: 80px;
      height: 80px;
      padding: 20px;

      svg {
        width: 40px;
        height: 40px;
      }
    }

    span {
      font-size: 16px;
    }
  }
`;

const ContactSection = styled.div`
  background: ${({ theme }) => theme.colors["vz-background-03"]};
  width: 100%;
  padding-bottom: 0;

  .container-blog {
    padding-bottom: 0;
  }

  .content {
    text-align: center;
    display: flex;
    flex-direction: column;
  }
  
  @media ${device.laptop} {
    .content {
      justify-content: space-between;
      text-align: left;
    }
    
    .container-blog {
      flex-direction: row;
      display: flex;
      padding-bottom: 40px;
      
      .content {
        width: 50%;
      }
      
      picture {
        position: relative;
        width: 50%;
      }
    }
  }

  @media ${device.laptopL} {
    .container-blog {  
      .content {
        width: 60%;
      }
      
      picture {
        width: 40%;
      }
    }

    .bg-whatsapp-iteractive {
      width: 658px;
      height: 694px;
      object-fit: cover;
    }
  }

  :is([data-theme="dark"] &) {
    background: ${({ theme }) => theme.colors["background-02"]};
  }
`;

const ContactTitle = styled.h3`
  font-size: 24px;
  line-height: 32px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors["neutral-07"]};
  margin-top: 16px;
  margin-bottom: 16px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -16px;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 32px;
    height: 4px;
    background: ${({ theme }) => theme.colors["branding-02"]};
    border-radius: 100px;
  }

  @media ${device.laptop} {
    font-size: 28px;
    line-height: 48px;
    margin-top: 24px;

    &::before {
      width: 48px;
      left: 0;
      transform: translate(0, -50%);
    }
  }

  :is([data-theme="dark"] &) {
    color: ${({ theme }) => theme.colors["neutral-01"]};

    &::before {
      background: ${({ theme }) => theme.colors["vivid-02"]};
    }
  }
`;

const ContactText = styled.p`
  font-size: 18px;
  line-height: 32px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors["neutral-05"]};
  margin-bottom: 24px;

  @media ${device.laptop} {
    max-width: 700px;

    font-size: 22px;
    line-height: 40px;
    margin-bottom: 40px;
  }

  :is([data-theme="dark"] &) {
    color: ${({ theme }) => theme.colors["neutral-01"]};
  }
`;

const StartButton = styled.a`
  display: flex;
  justify-content: center;
  align-self: center;
  align-items: center;
  gap: 8px;
  height: 48px;
  width: 342px;
  border-radius: 100px;
  transition: all 0.3s ease;
  background: ${({ theme }) => theme.colors["branding-02"]};
  font-size: 14px;
  letter-spacing: 1.4px;
  
  text-decoration: none;
  font-weight: 500;
  color: ${({ theme }) => theme.colors["neutral-01"]};
  
  &:hover {
    background: #7DAF2F;
    color: #FFF;
    text-decoration: none;
  }

  img {
    width: 20px;
    height: 20px;
  }

  @media ${device.laptop} {
    align-self: flex-start;
    width: 379px;
    height: 56px;
    font-size: 18px;
    letter-spacing: 1.8px;
    gap: 16px;
    line-height: 40px;

    img {
      width: 26px;
      height: 26px;
    }
  }
`;

const Wrapper = styled.div`
  width: 100%;
  position: relative;
`;

export function ShareAndContact({ relatedPosts }) {
	const { theme } = useTheme();
	const { width } = useWindowSize();
	const { t, i18n } = useTranslation();

	const isDarkTheme = theme === "dark";
	const isMobile = width <= size.mobileL;
	const language = i18n.language;

	const getImage = (screenSize) => {
		return `/blog/assets/png/contact/${language}/smartphone-vz-whatsapp-${theme}-${screenSize}.png`;
	};

	return (
		<Wrapper>
			<ShareSection>
				<div className="container-blog">
					<Title>{t("blog.shareSection.title")}</Title>
					<Subtitle>{t("blog.shareSection.subtitle")}</Subtitle>

					<SocialLinks>
						<SocialLink
							href={socialNetworks.instagram}
							aria-label="Compartilhar no Instagram"
						>
							<div className="circle">
								<InstagramLogo color={isDarkTheme ? "#FFF" : "#1D202A"} />
							</div>
							<span>{t("blog.shareSection.instagram")}</span>
						</SocialLink>
						{/* <SocialLink href={socialNetworks.x} aria-label="Compartilhar no X (Twitter)">
              <div className="circle">
                <XLogo color={isDarkTheme ? '#FFF' : '#1D202A'} />
              </div>
              <span>X</span>
            </SocialLink> */}
						<SocialLink
							href={socialNetworks.linkedin}
							aria-label="Compartilhar no LinkedIn"
						>
							<div className="circle">
								<LinkedinLogo color={isDarkTheme ? "#FFF" : "#1D202A"} />
							</div>
							<span>{t("blog.shareSection.linkedin")}</span>
						</SocialLink>
						<SocialLink
							href={socialNetworks.whatsapp()}
							aria-label="Compartilhar no WhatsApp"
						>
							<div className="circle">
								<WhatsappLogo color={isDarkTheme ? "#FFF" : "#1D202A"} />
							</div>
							<span>{t("blog.shareSection.whatsapp")}</span>
						</SocialLink>
					</SocialLinks>
				</div>
			</ShareSection>

			<ContactSection>
				<div className="container-blog">
					<div className="content">
						<ContactTitle>{t("blog.contactSection.title")}</ContactTitle>
						{isMobile ? (
							<ContactText>
								<Trans
									i18nKey="blog.contactSection.descriptionMobile"
									components={{ br: <br /> }}
								/>
							</ContactText>
						) : (
							<ContactText>
								<Trans
									i18nKey="blog.contactSection.description"
									components={{ br: <br /> }}
								/>
							</ContactText>
						)}
						<StartButton href={socialNetworks.whatsapp()}>
							<img src="/blog/assets/icons/whatsapp-white.svg" alt="" />
							{t("blog.contactSection.startChat")}
						</StartButton>
					</div>

					<picture className="mt-10 block w-[390px] relative md:left-0 sm:-left-[24px] mx-auto">
						<source media={device.laptop} srcSet={getImage("desktop")} />
						<img
							className={[
								"laptop:-bottom-10 laptop:-right-[100px] laptop:absolute laptop:h-auto laptop:w-[650px] laptop:object-[initial]",
								"laptopL:w-[690px] laptopM:max-h-[656px] laptopM:max-w-[628px]",
							]}
							src={getImage("mobile")}
							alt=""
						/>
					</picture>
				</div>
			</ContactSection>

			<RelatedPosts posts={relatedPosts} />
			<BackToTopButton />
		</Wrapper>
	);
}
